import { Base } from '../../abstract/base.model';
import { TestAnswer } from '../response-models/test-answer.model';
import { TestAttemptStatus } from './test-attempt-status.model';
import { isRecord } from '../utils/is-record';
import { toTestAnswers } from '../utils/safety-test-utils';
import { countOrZero, nullableBoolean, nullableNumber, nullableString, stringArrayOrEmpty } from '../utils/wire-value-utils';

/**
 * One user's attempt at a safety test, built from the hub's attempt JSON (`user-test-attempts-controller.ts`,
 * hub `data-models/test-attempt.ts`). Attempts change only through the start / answer / submit actions, so
 * every field is read-only; a `null` the hub sends reads as `undefined`.
 * `expiresAt` is when a pass expires; `timeLimitExpiresAt` is when the timer runs out.
 */
export class TestAttempt extends Base {
  testId: string;
  /** Only on `GET /test-attempts/my-attempts`. */
  testTitle?: string;
  profileId?: string;
  organizationId?: string;
  startedAt?: string;
  completedAt?: string;
  status: TestAttemptStatus;
  score?: number;
  passed?: boolean;
  correctAnswers?: number;
  totalQuestions: number;
  currentQuestionIndex: number;
  questionIds: string[];
  answeredQuestionIds: string[];
  timeLimitExpiresAt?: string;
  remainingSeconds?: number;
  timeSpentSeconds?: number;
  expiresAt?: string;
  /** True when starting returned the user's attempt already in progress. */
  resumed: boolean;
  answers: TestAnswer[];

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.testId = nullableString(source['testId']) ?? '';
    this.testTitle = nullableString(source['testTitle']) ?? undefined;
    this.profileId = nullableString(source['profileId']) ?? undefined;
    this.organizationId = nullableString(source['organizationId']) ?? undefined;
    this.startedAt = nullableString(source['startedAt']) ?? undefined;
    this.completedAt = nullableString(source['completedAt']) ?? undefined;
    this.status = (nullableString(source['status']) ?? 'in_progress') as TestAttemptStatus;
    this.score = nullableNumber(source['score']) ?? undefined;
    this.passed = nullableBoolean(source['passed']) ?? undefined;
    this.correctAnswers = nullableNumber(source['correctAnswers']) ?? undefined;
    this.totalQuestions = countOrZero(source['totalQuestions']);
    this.currentQuestionIndex = countOrZero(source['currentQuestionIndex']);
    this.questionIds = stringArrayOrEmpty(source['questionIds']);
    this.answeredQuestionIds = stringArrayOrEmpty(source['answeredQuestionIds']);
    this.timeLimitExpiresAt = nullableString(source['timeLimitExpiresAt']) ?? undefined;
    this.remainingSeconds = nullableNumber(source['remainingSeconds']) ?? undefined;
    this.timeSpentSeconds = nullableNumber(source['timeSpentSeconds']) ?? undefined;
    this.expiresAt = nullableString(source['expiresAt']) ?? undefined;
    this.resumed = nullableBoolean(source['resumed']) ?? false;
    this.answers = toTestAnswers(source['answers']);
  }
}
